import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Radiobutton from '@/components/RadioButton';
import { PROPERTYLIST } from '@/data/constants';
import { useSaveMutation } from '@/services/api';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import validate from '@/helpers/validations';
import { getMutationResult } from '@/helpers/query';
import { Page, ProcessHeader } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import ProcessButton from '@/components/ProcessButton';
import ReasonIcon from '@/assets/images/reasonIcon.svg?react'; // eslint-disable-line import/no-unresolved

const Reason = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [optionsArray, setOptionsArray] = useState([]);
  const dispatch = useDispatch();
  const [apiCg] = useSaveMutation();
  const navigate = useNavigate();
  const { extraDetails, id } = useSelector((state) => state.proposal);
  const loanOptions = useSelector((state) => state.loanOptions);
  const { role } = useSelector((state) => state.user);
  useIdInStoreGuard();

  const required = t('globals.validations.required');

  const formValidations = {
    loanTypeName: (value) => validate(value, { required }),
  };

  const initialValues = {
    loanTypeName: extraDetails.loanTypeName,
    loanTypeNameHebrew: extraDetails.loanTypeNameHebrew,
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues,
    validate: formValidations,
    validateInputOnSubmit: true,
  });

  useEffect(() => {
    if (loanOptions) {
      let array = loanOptions?.map((option) => {
        const selectedOption = PROPERTYLIST.filter((item) => item.value === option.title);
        return {
          icon: selectedOption[0]?.icon,
          value: option.title,
          id: option._id,
          label: option.hebrew,
          description: option.description,
          order: option.order,
        };
      });

      if (role.code === '5') {
        array = array.filter((option) => option.value !== 'businessCredit');
      }

      setOptionsArray(array);
    }
  }, [loanOptions]);

  const { setFieldValue } = form;

  const handleSubmit = async (value) => {
    setLoading(true);

    const loanId = loanOptions.find((loan) => loan.title === value.loanTypeName)?._id;

    const objectToSend = {
      loan: loanId,
      id,
    };

    const { isError } = await getMutationResult(objectToSend, apiCg);

    if (isError) {
      setLoading(false);

      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }

    dispatch(updateExtraDetails({ ...value, isRecycling: null }));

    const routeMap = {
      mortgageRefinancing: 'bankingConduct',
      businessCredit: 'businessCredit',
      replacementProperty: 'existingMortgage',
      anyPurposeWithPropertyCollateral: 'existingMortgage',
      renovationOrExpansion: 'existingMortgage',
    };

    const routeKey = routeMap[value.loanTypeName] || 'assetValue';
    navigate(routeWithParams(getRouteById(routes.newProposal.id, routeKey).path, { id }));
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'borrowerWithId').path, { id }));
  };

  return (
    <ProcessHeader
      headerText="מטרת ההלוואה"
      headerColor="#F6EBFF"
      headerTextColor="#9F1FF4"
      subtitle="נא לבחור עבור מה מבקש הלקוח הלוואה"
      showReturnButton
      onReturn={handleReturn}
      icon={ReasonIcon}
    >
      <form onSubmit={form.onSubmit((v) => handleSubmit(v))}>
        <Page.Section >
          <Radiobutton
            options={[...optionsArray].sort((a, b) => a.order - b.order)}
            value={form.values.loanTypeName}
            form={form}
            fieldName="loanTypeName"
            onChange={(value) => {
              const loan = optionsArray.filter(
                (loanTypeObject) => loanTypeObject.value === value,
              );

              setFieldValue('loanTypeNameHebrew', loan[0].label);
              setFieldValue('loan', loan[0].id);
            }}
            variant='reason'
          />
        </Page.Section>

        <Page.Actions>
          <ProcessButton type="submit" loading={loading} />
        </Page.Actions>
      </form>
    </ProcessHeader>
  );
};

export default Reason;
